import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Truck,
    ChevronRight,
    ClipboardCheck,
    Plus,
    Hourglass,
} from 'lucide-react';
import { TbX } from "react-icons/tb";
import { GetSession } from '../CommonComponents/criptoUtils';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall, getDateAndTime, handleDateChange, initializeFilterData, sanitizeInput } from '../CommonComponents/CustomHooks';
import { CommonApiData } from '../CommonComponents/EnvConfig';
import { AutoCompleteBoxNew, DateBox, FormButton, InputBox } from '../CommonComponents/InputBox';
import { FiRotateCcw, FiSearch } from "react-icons/fi";
import { Table } from '../CommonComponents/Table';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { handleDownloadExcel } from '../CommonComponents/ExportData';
import Skeleton from 'react-loading-skeleton';


const PickupCard = ({
    label,
    value,
    accent,
    icon: Icon,
    iconBg,
    iconColor,
    onClick
}) => {
    return (
        <div
            onClick={onClick}
            className={`
                min-w-0
                border border-slate-200/90 
                ${accent}
                rounded-xl p-2.5 sm:p-3
                bg-white 
                flex items-center justify-between
                gap-2
                shadow-2xs cursor-pointer
                hover:bg-slate-50 
                transition-colors
            `}
        >
            <div className="min-w-0 flex flex-col justify-between">
                <span className="text-[9px] sm:text-[10px] font-bold text-slate-400  uppercase tracking-wider leading-tight">
                    {(label)}
                </span>

                <span className="text-xl sm:text-2xl font-black text-slate-900  leading-tight mt-1">
                    {value}
                </span>

                <span className="text-[10px] sm:text-[10.5px] font-bold text-blue-600 -400 mt-1 flex items-center gap-0.5">
                    <span>{("View Details")}</span>
                    <ChevronRight className="w-2.5 h-2.5 shrink-0" />
                </span>
            </div>

            <div
                className={`
                mt-3
                    w-8 h-8 sm:w-9 sm:h-9
                    rounded-xl ${iconBg} ${iconColor}
                    flex items-center justify-center
                    shrink-0
                `}
            >
                <div className="w-8 h-8 sm:w-9 sm:h-9 m-1 p-1.5 sm:p-2 rounded-xl flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                </div>
            </div>
        </div>
    );
};

const PickupDashboard = () => {
    const navigate = useNavigate();
    const user = GetSession('cookies', 'clientUser');
    const userName = user?.userName || user?.loginUserName;
    //copy
    const [subLoading, setSubLoading] = useState(false);
    // 1. Update state initialization
    const [stats, setStats] = useState({
        totalPickupRequest: "0",
        unscheduledPickup: "0",
        awaitingPickup: "0",
        inTransit: "0",
        shortcuts: [
            {
                id: "pickupRequests",
                title: "Create Pickup Request",
                icon: "file",
                route: "/Pickup/PickupRequests",
                borderLeft: "border-l-2 border-l-purple-600",
                iconBg: "bg-[#f5f3ff] text-[#7c3aed]  "
            },
            {
                id: "assignVehicle",
                title: "Assign Vehicle",
                icon: "truck",
                route: "/Pickup/AssignVehicle",
                borderLeft: "border-l-2 border-l-indigo-600",
                iconBg: "bg-[#eef4ff] text-[#2563eb]  "
            }
        ]
    });
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData)
    const [showModalBox, setShowModalBox] = useState(false)

    const [tableData, setTableData] = useState([])
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCardDetails, setSelectedCardDetails] = useState("")

    const [exportData, setExportData] = useState({
        CallType: ""
    });
    const inputrefs = useRef([])
    const initialFormData = {
        fromDate: getDateAndTime(7).date,
        toDate: getDateAndTime().date,
        pickupRequestNo: ""
    }
    const [formData, setFormData] = useState(initialFormData);

    const extraPayload = {
        ...filterData,
        ...formData,
        "loginUserBCode": CommonApiData().loginUserBCode,
    }

    const getPickupDashboardData = async () => {
        try {
            setSubLoading(true);
            let GetData = {
                Type: "S",
                ...formData,
            };
            let data = await ApiCall('/api/PickupRequest/GetPickupRequestDashboardCount', GetData);
            if (data?.status === 'EXPIRED') {
                NotificationSound(data.status, data.message)
                return;
            }
            setStats({
                ...data?.data, shortcuts: [
                    {
                        id: "pickupRequests",
                        title: "Create Pickup Request",
                        icon: "file",
                        route: "/Pickup/PickupRequests",
                        borderLeft: "border-l-2 border-l-purple-600",
                        iconBg: "bg-[#f5f3ff] text-[#7c3aed] "
                    },
                    {
                        id: "assignVehicle",
                        title: "Assign Vehicle",
                        icon: "truck",
                        route: "/Pickup/AssignVehicle",
                        borderLeft: "border-l-2 border-l-indigo-600",
                        iconBg: "bg-[#eef4ff] text-[#2563eb] "
                    }
                ]
            });
        } catch (error) {
            console.error("Dashboard Count Error:", error);
        } finally {
            setSubLoading(false);
        }
    };
    const handleCardClick = async (CurrentPage = currentPageNo, callType, dashboardName, freshSortConfig) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        const data = {
            Type: "S",
            ...formData,
            ...filterData,
            CallType: callType || "",
            PageIndex: `${PageIndex}`,
            ...(freshSortConfig || sortConfig),
        };
        setExportData(prev => ({
            ...prev,
            CallType: callType || ""
        }));
        setIsLoading(true)
        let res = await ApiCall(`/api/PickupRequest/GetPickupRequestDashboardGridDetails`, data)
        if (res.Status === 'SUCCESS') {
            setTableData(res);
            setSelectedCardDetails(dashboardName)
            setShowModalBox(true)
        } else if (res.Status === 'ERROR') {
            setTableData([]);
            setSelectedCardDetails("")
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED') {
            await NotificationSound(res.Status, res.Message, res.focus, inputrefs);
        }
    }

    const handleChange1 = (event, value) => {
        handleCardClick(value, exportData.CallType, selectedCardDetails);
    };


    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig?.SortColumn === SortColumn && sortConfig?.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        const newSortConfig = { SortColumn, SortOrder };
        setSortConfig(newSortConfig);

        handleCardClick(currentPageNo, exportData.CallType, selectedCardDetails, newSortConfig);
    };

    const handleModalDocShowClose = () => {
        setShowModalBox(false);
        setTableData([]);
        setSelectedCardDetails("");
        setCurrentPageNo(1);
    };

    const handleReset = async () => {
        await setFormData(initialFormData)
        getPickupDashboardData()
    }

    useEffect(() => {
        document.title = "Pickup Dashboard";
        inputrefs.current['txtFromDate']?.focus();
        getPickupDashboardData();
    }, []);

    return (
        <>
            <div className="w-full mx-auto p-1 space-y-3">
                {/* ── Premium Compact Header Banner (Exact match to Main Dashboard) ── */}
                <div className="relative overflow-hidden rounded-xl py-2 px-4 shadow-sm border border-sky-100  bg-sky-50/80 ">
                    <div className="relative flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-sky-900  text-lg font-extrabold tracking-tight">
                                {("Pickup Dashboard")}
                            </h1>
                            <span className="flex h-2.5 w-2.5 relative">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Premium Compact Filter Panel */}
                <div className="bg-white border border-gray-100  rounded-xl p-2.5 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2.5 items-end">
                        <div className="lg:col-span-1">
                            <DateBox
                                inputrefs={inputrefs}
                                label="From Date"
                                id='txtFromDate'
                                maxDate={new Date()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        inputrefs.current['txtToDate'].focus();
                                    }
                                }}
                                selected={formData.fromDate === '' ? '' : new Date(formData.fromDate)}
                                onChange={(e) => handleDateChange(e, setFormData, formData, 'fromDate')}
                            />
                        </div>
                        <div className="lg:col-span-1">
                            <DateBox
                                inputrefs={inputrefs}
                                label="To Date"
                                id='txtToDate'
                                maxDate={new Date()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        inputrefs.current['txtClient']?.focus();
                                    }
                                }}
                                selected={formData.toDate === '' ? '' : new Date(formData.toDate)}
                                onChange={(e) => handleDateChange(e, setFormData, formData, 'toDate')}
                            />
                        </div>

                        <div className="lg:col-span-2">
                            <InputBox
                                id="txtPickupRequestNo"
                                label="Pickup Request No."
                                placeholder="Pickup Request No."
                                value={formData.pickupRequestNo}
                                maxLength='10'
                                onChange={(e) =>
                                    setFormData({ ...formData, pickupRequestNo: sanitizeInput(e.target.value) })
                                }
                                inputrefs={inputrefs}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        inputrefs.current['btn']?.focus();
                                    }
                                }}
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                ref={(el) => inputrefs.current['btn'] = el}
                                id="btn"
                                onClick={getPickupDashboardData}
                                className="cursor-pointer h-8 flex-1 text-white rounded-lg flex items-center justify-center gap-1 transition-all duration-300 bg-emerald-500 hover:bg-emerald-600 hover:shadow-md hover:shadow-emerald-500/10 active:scale-95"
                                title="Search"
                            >
                                <FiSearch className="text-sm" />
                                <span className="text-xs font-bold">Apply</span>
                            </button>
                            <button
                                onClick={handleReset}
                                className="cursor-pointer h-8 w-8 text-gray-500  border border-gray-100  rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-gray-50  active:scale-95"
                                title="Reset"
                            >
                                <FiRotateCcw className="text-sm" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Main Compact White Container Wrapper ─────────────────────── */}
                <div className="bg-white border border-slate-200/90  rounded-2xl p-4 sm:p-5 shadow-xs space-y-4">

                    {/* ── Greeting Row (Greeting + Subtitle) ── */}
                    <div>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-[#0f172a] tracking-tight flex items-center gap-1.5 leading-tight">
                            <span>{("Hi")}, {userName}</span>
                            <span className="inline-block animate-wave text-xl sm:text-2xl">👋</span>
                        </h2>
                        <p className="text-[11.5px] sm:text-xs text-slate-500  font-normal mt-0.5">
                            {(stats?.welcomeSubtitle)}
                        </p>
                    </div>

                    {/* ── Middle Row: Actions Card (Left) & Shortcuts Card (Right) ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
                        {/* ── Left Card: Actions ── */}
                        <div className="bg-[#fcfdfe]  border border-slate-200/80 rounded-xl p-3.5 sm:p-4 shadow-2xs flex flex-col">
                            {/* Header */}
                            <div className="flex items-center gap-2 pb-2.5">
                                <div className="w-7 h-7 rounded-full bg-[#edf4ff]  text-[#2563eb] flex items-center justify-center">
                                    <ClipboardCheck className="w-4 h-4 stroke-[2]" />
                                </div>
                                <h2 className="text-sm font-bold text-slate-900 ">
                                    {("Actions")}
                                </h2>
                            </div>

                            {/* Stats as 3 Cards with Left Border & Right Icon */}
                            <div className="mb-3">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 px-1">Pickup</p>
                                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                                    {subLoading ? (
                                        [...Array(4)].map((_, i) => (
                                            <div key={i} className="p-4 border rounded-xl bg-white shadow-sm">
                                                <Skeleton height={15} width={80} />
                                                <Skeleton height={25} width={60} style={{ marginTop: 10 }} />
                                            </div>
                                        ))
                                    ) : (
                                        <>
                                            <PickupCard label="Total Pickup Requests" value={stats?.totalPickupRequest || "0"} accent="border-l-4 border-l-blue-500" icon={Truck} iconBg="bg-blue-50 " iconColor="text-blue-600 0" onClick={() => handleCardClick(currentPageNo, "GetTotalPickupRequest", "Pickup")} />
                                            <PickupCard label='Unscheduled Pickup' value={stats?.unscheduledPickup || "0"} accent="border-l-4 border-l-emerald-500" icon={ClipboardCheck} iconBg="bg-emerald-50 " iconColor="text-emerald-600 " onClick={() => handleCardClick(currentPageNo, "GetUnscheduledPickup", "Un Pickup")} />
                                            <PickupCard label='Awaiting Pickup' value={stats?.awaitingPickup || "0"} accent="border-l-4 border-l-amber-500" icon={Hourglass} iconBg="bg-amber-50 " iconColor="text-amber-600 " onClick={() => handleCardClick(currentPageNo, "GetAwaitingPickup", "OFP")} />
                                            <PickupCard label='In Transit' value={stats?.inTransit || "0"} accent="border-l-4 border-l-red-500" icon={Truck} iconBg="bg-red-50 " iconColor="text-red-600 " onClick={() => handleCardClick(currentPageNo, "GetInTransit", "Picked Up")} />
                                        </>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* ── Right Card: Shortcuts (With Left Border Accent) ── */}
                        <div className="bg-[#fcfdfe]  border border-slate-200/80  rounded-xl p-3.5 sm:p-4 shadow-2xs flex flex-col">
                            {/* Header */}
                            <div className="flex items-center gap-2 pb-2.5">
                                <div className="w-7 h-7 rounded-full bg-[#f3eeff]  text-[#7c3aed] flex items-center justify-center">
                                    <Truck className="w-4 h-4 stroke-[2]" />
                                </div>
                                <h2 className="text-sm font-bold text-slate-900">
                                    {("Shortcuts")}
                                </h2>
                            </div>

                            {/* 2 Shortcut Action Cards with border-left */}
                            <div className="grid grid-cols-2 gap-2.5">
                                {stats?.shortcuts?.map((sc) => (
                                    <button
                                        key={sc.id}
                                        type="button"
                                        onClick={() => navigate(sc.route)}
                                        className={`min-w-0 border border-slate-200/90   ${sc.borderLeft} rounded-xl p-2.5 sm:p-3  flex flex-col justify-between min-h-[88px] bg-white   hover:shadow-xs transition-all duration-200  text-left cursor-pointer group `}
                                    >
                                        <div className={`w-7 h-7 rounded-lg ${sc.iconBg} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                                            {sc.id === 'pickupRequests' ? (
                                                <Plus className="w-4 h-4 stroke-[2.5]" />
                                            ) : (
                                                <Truck className="w-4 h-4 stroke-[2]" />
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between text-xs font-bold text-slate-900  group-hover:text-[#7c3aed] transition-colors">
                                            <span className="truncate pr-1">
                                                {(sc.title)}
                                            </span>
                                            <ChevronRight className="w-3 h-3 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            {/* Detail Table Dialog */}
            <Dialog
                open={showModalBox}
                onClose={handleModalDocShowClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth={false}
                sx={{
                    "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1000px" } },
                    "& .css-hz1bth-MuiDialog-container": { alignItems: 'center' },
                    "& .MuiPaper-root": {
                        width: '95vw',
                        maxWidth: '1000px',
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid rgba(0,0,0,0.05)'
                    },
                }}>
                <DialogTitle id="alert-dialog-title" className="p-2.5 px-4 border-b border-gray-100  flex justify-between items-center bg-gray-50/50 ">
                    <div className="font-extrabold text-base text-gray-800 tracking-tight">
                        {selectedCardDetails ? `${selectedCardDetails} Details` : "Dashboard Details"}
                    </div>
                    <div className="flex items-center gap-3">
                        <FormButton
                            extraclassname="m-0 p-0"
                            id={'btnSub'}
                            inputrefs={inputrefs}
                            handleExport={async () => {
                                const payload = { ...extraPayload, ...exportData };
                                setSubLoading(true)
                                await handleDownloadExcel(`/api/PickupRequest/GetPickupRequestDashboardGridDetails`, payload, selectedCardDetails ? `${selectedCardDetails} Details` : "Dashboard Details")
                                setSubLoading(false)
                            }}
                            save={false} search={false} Export={true} Reset={false}
                        />
                        <button
                            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100  transition-colors duration-200 flex items-center justify-center"
                            onClick={handleModalDocShowClose}
                            title="Close"
                        >
                            <TbX size={18} />
                        </button>
                    </div>
                </DialogTitle>
                <DialogContent className='p-3 pt-1 bg-white '>
                    <Table
                        loading={isLoading}
                        PageName={'PickupDashboard'}
                        handleFilter={() => { handleCardClick(currentPageNo, exportData.CallType, selectedCardDetails); setFilterData({ ...filterData }) }}
                        filterData={filterData}
                        tableData={tableData}
                        setFilterData={setFilterData}
                        currentPageNo={currentPageNo}
                        handleChange1={handleChange1}
                        handleSorting={handleSorting}
                        sortConfig={sortConfig}
                        Filter={false}
                        onlyFilterPage={true}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PickupDashboard;
